import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DropdownMenu } from '@hotjar/hotsauce';
import type { HighlightsListItem } from '@hotjar/webapp/domains/highlights/model';

import { useSetHighlightSolved } from '../SingleHighlight/use-set-highlight-solved';
import { useDownloadRecordingHighlight } from './use-download-recording-highlight';
import { HeatmapHighlightDownload } from './HeatmapHighlightDownload';
import {
  ActionIcon,
  ColoredText,
  HighlightActionsTrigger,
} from './HighlightCardActionsDropdownStyles';

interface HighlightCardActionsDropdownProps {
  highlight: HighlightsListItem;
  setDropdownHighlightId: (id: string) => void;
  setIsShortLinkWidgetShown: (isShown: boolean) => void;
  currentUserId: number | undefined;
  startRemoval: () => void;
  isHovered: boolean;
  siteId: string;
}

export const HighlightCardActionsDropdown = ({
  highlight,
  setDropdownHighlightId,
  setIsShortLinkWidgetShown,
  currentUserId,
  startRemoval,
  isHovered,
  siteId,
}: HighlightCardActionsDropdownProps): JSX.Element => {
  const { t } = useTranslation();

  const { setHighlightSolved } = useSetHighlightSolved(siteId);

  const [svgVisible, setSvgVisible] = useState(false);
  const downloadRecordingHighlight = useDownloadRecordingHighlight(highlight, 'highlight_card');

  const authorIsCurrentUser = highlight?.author?.id === String(currentUserId);

  return (
    <>
      {highlight.type === 'heatmap' && svgVisible && (
        <HeatmapHighlightDownload
          siteId={siteId}
          highlightId={highlight.id}
          imageUrl={highlight.imageUrl ?? ''}
          selectedArea={highlight.metadata.selectedArea}
          afterDownload={() => setSvgVisible(false)}
          trackingUiPosition="highlight_card"
        />
      )}

      <DropdownMenu
        trigger={
          <HighlightActionsTrigger
            description={t('highlights:single.actions')}
            isVisible={isHovered}
            icon="menu_kebab"
            kind="ghost"
            size="m"
            tooltipText={t('collections:actions.description')}
          />
        }
        appendTo="parent"
      >
        <DropdownMenu.BaseItem
          aria-label={t('common:actions.share')}
          onClick={() => {
            setDropdownHighlightId(highlight.id);
            setIsShortLinkWidgetShown(true);
          }}
        >
          <ActionIcon icon="share" color="text_default" size="m" />
          <ColoredText color="text_default">{t('common:actions.share')}</ColoredText>
        </DropdownMenu.BaseItem>
        <DropdownMenu.BaseItem
          onClick={() => {
            if (highlight.type === 'heatmap') {
              setSvgVisible(true);
            } else {
              downloadRecordingHighlight();
            }
          }}
        >
          <ActionIcon icon="download" color="text_default" size="m" />
          <ColoredText color="text_default">{t('highlights:single.download')}</ColoredText>
        </DropdownMenu.BaseItem>
        <DropdownMenu.BaseItem
          onClick={() => setHighlightSolved(highlight.id, !highlight.solved, 'highlight_card')}
        >
          <ActionIcon
            icon={!highlight.solved ? 'check' : 'check_alt'}
            color={!highlight.solved ? 'text_default' : 'text_success'}
            size="m"
          />
          <ColoredText color="text_default">
            {!highlight.solved
              ? t('highlights:single.mark_solved')
              : t('highlights:single.unmark_solved')}
          </ColoredText>
        </DropdownMenu.BaseItem>
        {authorIsCurrentUser ? (
          <DropdownMenu.BaseItem
            aria-label={t('highlights:single.remove.description')}
            onClick={() => {
              setDropdownHighlightId(highlight.id);
              startRemoval();
            }}
            hasDivider
          >
            <ActionIcon icon="trash" color="text_warning" size="m" />
            <ColoredText color="text_warning">{t('highlights:single.remove.label')}</ColoredText>
          </DropdownMenu.BaseItem>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
      </DropdownMenu>
    </>
  );
};
